import axios from 'axios';
export {
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  DEFAULT_CREATED_RANGE,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  parseLocalDateParam,
  tenantDisplayName,
  tenantOptionLabel,
} from './auditFilters';

/**
 * Job-audit API client.
 *
 * Served by the job-audit service, not this platform. Relative paths only:
 * nginx (prod) and src/setupProxy.js (local) split /api/audit off so the
 * HttpOnly delair_access_token cookie is sent same-origin.
 */

const BASE = '/api/audit';

export const listAuditRuns = async ({
  tenant = '',
  status = '',
  band = '',
  job = '',
  technician_id = '',
  created_after = '',
  created_before = '',
  limit = 50,
  offset = 0,
} = {}) => {
  const params = { limit, offset };
  if (tenant) params.tenant = tenant;
  if (status) params.status = status;
  if (band) params.band = band;
  if (job) params.job = job;
  if (technician_id) params.technician_id = technician_id;
  if (created_after) params.created_after = created_after;
  if (created_before) params.created_before = created_before;
  const { data } = await axios.get(`${BASE}/runs`, { params });
  return data;
};

export const getAuditSummary = async ({
  tenant = '',
  created_after = '',
  created_before = '',
} = {}) => {
  const params = {};
  if (tenant) params.tenant = tenant;
  if (created_after) params.created_after = created_after;
  if (created_before) params.created_before = created_before;
  const { data } = await axios.get(`${BASE}/summary`, { params });
  return data;
};

export const getWorkOnItem = async (
  itemId,
  { tenant = '', created_after = '', created_before = '' } = {}
) => {
  const params = {};
  if (tenant) params.tenant = tenant;
  if (created_after) params.created_after = created_after;
  if (created_before) params.created_before = created_before;
  const { data } = await axios.get(
    `${BASE}/summary/items/${encodeURIComponent(itemId)}`,
    { params }
  );
  return data;
};

export const getWorkOnTechnicians = async (
  itemId,
  { tenant = '', field = '', created_after = '', created_before = '' } = {}
) => {
  const params = {};
  if (tenant) params.tenant = tenant;
  if (field) params.field_id = field;
  if (created_after) params.created_after = created_after;
  if (created_before) params.created_before = created_before;
  const { data } = await axios.get(
    `${BASE}/summary/items/${encodeURIComponent(itemId)}/technicians`,
    { params }
  );
  return data;
};

export const listTechnicians = async ({
  tenant = '',
  created_after = '',
  created_before = '',
  confidence = '',
  max_score = '',
  limit = 50,
  offset = 0,
} = {}) => {
  const params = { limit, offset };
  if (tenant) params.tenant = tenant;
  if (created_after) params.created_after = created_after;
  if (created_before) params.created_before = created_before;
  if (confidence) params.confidence = confidence;
  if (max_score !== '' && max_score != null) params.max_score = max_score;
  const { data } = await axios.get(`${BASE}/technicians`, { params });
  return data;
};

export const listScorePolicies = async () => {
  const { data } = await axios.get(`${BASE}/scoring/policies`);
  return data;
};

export const getScorePolicy = async (policyId, policyVersion = '') => {
  const params = {};
  if (policyVersion) params.policy_version = policyVersion;
  const { data } = await axios.get(
    `${BASE}/scoring/policies/${encodeURIComponent(policyId)}`,
    { params }
  );
  return data;
};

export const saveScorePolicy = async (policyId, payload) => {
  const { data } = await axios.put(
    `${BASE}/scoring/policies/${encodeURIComponent(policyId)}`,
    payload
  );
  return data;
};

export const getAuditRun = async (auditRunId) => {
  const { data } = await axios.get(`${BASE}/runs/${auditRunId}`);
  return data;
};

export const saveFindingReview = async (findingId, payload) => {
  const { data } = await axios.post(`${BASE}/findings/${findingId}/review`, payload);
  return data;
};

export const attachmentUrl = (auditRunId, attachmentId) =>
  `${BASE}/runs/${auditRunId}/attachments/${encodeURIComponent(attachmentId)}`;

export const fieldPreviewUrl = (auditRunId, fieldId) =>
  `${BASE}/runs/${auditRunId}/fields/${encodeURIComponent(fieldId)}/preview`;

export const apiMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
    return detail.message;
  }
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

export const createProgressPreview = async ({ tenantId, technicianId, asOf = '' }) => {
  const body = { tenant_id: tenantId, technician_id: technicianId };
  if (asOf) body.as_of = asOf;
  const { data } = await axios.post(`${BASE}/notifications/technician-progress/previews`, body);
  return data;
};

export const sendProgressPreview = async (previewId) => {
  const { data } = await axios.post(
    `${BASE}/notifications/technician-progress/previews/${encodeURIComponent(previewId)}/send`
  );
  return data;
};

export const listNotificationSettings = async () => {
  const { data } = await axios.get(`${BASE}/admin/technician-notifications/settings`);
  return data;
};

export const saveNotificationSettings = async (tenantId, payload) => {
  const { data } = await axios.put(
    `${BASE}/admin/tenants/${encodeURIComponent(tenantId)}/technician-notifications/settings`,
    payload
  );
  return data;
};

export const listNotificationRuns = async (tenantId) => {
  const { data } = await axios.get(
    `${BASE}/admin/tenants/${encodeURIComponent(tenantId)}/technician-notifications/runs`
  );
  return data;
};

export const STATUS_OPTIONS = [
  'technician_correction_required',
  'critical_failure',
  'human_review_required',
  'accepted',
  'accepted_with_warnings',
  'audit_failed',
];

export const BAND_OPTIONS = ['low', 'medium', 'high', 'pending', 'unscored'];

export const FINDING_STATUSES = [
  'pass',
  'fail',
  'missing',
  'warning',
  'not_applicable',
  'unable_to_verify',
  'human_review_required',
];

export const SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'];
